import type { ReactNode } from "react"

import ProductImage from "~/components/reusable/ProductImage"
import { cn } from "~/utils/helpers"

type ItemIdentityProps = {
  /** Truncates rather than wrapping. */
  title: ReactNode
  /** An id, a code, a category. */
  subtitle?: ReactNode
  /** Omit (undefined) to drop the image slot; null still reserves it. */
  imageUrl?: string | null
  /** px. 28 matches the table cells. */
  imageSize?: number
  /** Table cells use `gap-4`. */
  gapClassName?: string
  className?: string
  tile?: boolean
}

/**
 * Who an item is: image, name, and a secondary line, as in the product and variant table cells.
 * Use it wherever a surface names a single item (inspector headers, steppers, pickers, dialogs).
 * Takes plain nodes, not a variant, so non-variant items fit without faking a variant shape.
 *
 * Approved surface: design-system/APPROVED.md.
 */
export function ItemIdentity({
  title,
  subtitle,
  imageUrl,
  imageSize = 28,
  gapClassName = "gap-3",
  className,
  tile = false,
}: ItemIdentityProps) {
  return (
    <div className={cn("flex min-w-0 flex-row items-center", gapClassName, className)}>
      {imageUrl !== undefined && (
        <div className="relative ml-0.5 shrink-0" style={{ minWidth: imageSize }}>
          <ProductImage url={imageUrl} alt="" width={imageSize} tile={tile} />
        </div>
      )}
      <div className="min-w-0 leading-5">
        <div className="label-sm truncate">{title}</div>
        {subtitle != null && <div className="paragraph-sm truncate text-fg-muted">{subtitle}</div>}
      </div>
    </div>
  )
}

export default ItemIdentity
